"use client"
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useCurrentUser } from '@/hooks/use-current-user';
import { firestoreRef } from '@/firebase/db';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  preferences: CookiePreferences;
  updatePreferences: (newPreferences: CookiePreferences) => Promise<void>;
  hasConsent: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user] = useCurrentUser();
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    const loadPreferences = async () => {
      // First check localStorage
      const storedPreferences = localStorage.getItem('cookieConsent');
      if (storedPreferences) {
        setPreferences(JSON.parse(storedPreferences));
        setHasConsent(true);
        return;
      }

      // If user is logged in, try to load from Firebase
      if (user) {
        try {
          const userDocRef = doc(firestoreRef, 'profile', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists() && userDoc.data().cookiePreferences) {
            const firebasePreferences = userDoc.data().cookiePreferences;
            setPreferences(firebasePreferences);
            localStorage.setItem('cookieConsent', JSON.stringify(firebasePreferences));
            setHasConsent(true);
          }
        } catch (error) {
          console.error('Error loading cookie preferences from Firebase:', error);
        }
      }
    };

    loadPreferences();
  }, [user]);

  const updatePreferences = async (newPreferences: CookiePreferences) => {
    setPreferences(newPreferences);
    localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
    setHasConsent(true);

    if (user) {
      try {
        const userDocRef = doc(firestoreRef, 'profile', user.uid);
        await setDoc(userDocRef, {
          cookiePreferences: newPreferences,
          updatedAt: new Date().toISOString(),
        }, { merge: true });
      } catch (error) {
        console.error('Error saving cookie preferences to Firebase:', error);
      }
    }
  };

  return (
    <CookieConsentContext.Provider value={{ preferences, updatePreferences, hasConsent }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}; 